// src/pages/Dashboard.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../components/AuthContext';
import EmojiLoader from '../components/EmojiLoader';
import FilterModal from '../components/FilterModal';
import axios from 'axios';
import { FaSync, FaGift, FaExternalLinkAlt, FaDownload, FaTags } from 'react-icons/fa';
import { FiInfo, FiChevronDown } from 'react-icons/fi';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts';
import './Dashboard.css';
import NicheModal from '../components/NicheModal';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000';

/* ---- Media query hook ---- */
function useMedia(query) {
  const get = () =>
    typeof window !== 'undefined' && window.matchMedia(query).matches;
  const [matches, setMatches] = useState(get);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia(query);
    const onChange = (e) => setMatches(e.matches);
    try { mql.addEventListener('change', onChange); }
    catch { mql.addListener(onChange); }
    return () => {
      try { mql.removeEventListener('change', onChange); }
      catch { mql.removeListener(onChange); }
    };
  }, [query]);
  return matches;
}

/* ---- “ver como” a partir da URL ---- */
function getAsFromLocation() {
  if (typeof window === 'undefined') return null;
  const fromSearch = new URLSearchParams(window.location.search).get('as');
  if (fromSearch) return fromSearch;
  if (window.location.hash) {
    const qIndex = window.location.hash.indexOf('?');
    if (qIndex >= 0) {
      const fromHash = new URLSearchParams(window.location.hash.slice(qIndex)).get('as');
      if (fromHash) return fromHash;
    }
  }
  const m =
    window.location.pathname.match(/\/dashboard\/view\/([a-f0-9]{24})/i) ||
    window.location.pathname.match(/\/dashboard\/([a-f0-9]{24})/i);
  return m ? m[1] : null;
}

/* ---------------------------------------------------- */
const METRICS = [
  {
    id: 'cliques',
    label: 'Cliques',
    color: '#4f46e5',
    format: (n) => Number(n || 0).toLocaleString('pt-BR'),
    info: 'Total de cliques no período selecionado.',
  },
  {
    id: 'comissao',
    label: 'Comissão',
    color: '#10b981',
    format: (n) =>
      `R$ ${Number(n || 0).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
    info: 'Comissão bruta gerada no período selecionado.',
  },
];

function getStatusMeta(status) {
  const key = String(status || '').toUpperCase();
  if (['PAID', 'APPROVED', 'COMPLETED'].includes(key)) {
    const label = key === 'COMPLETED' ? 'Concluído' : 'Pago';
    return { label, cls: 'paid', commCls: 'is-paid' };
  }
  if (['PENDING', 'ON_HOLD', 'WAITING'].includes(key)) {
    return {
      label: key === 'PENDING' ? 'Pendente' : key === 'ON_HOLD' ? 'Em análise' : 'Aguardando',
      cls: 'pending',
      commCls: 'is-pending'
    };
  }
  if (['CANCELLED', 'CANCELED', 'REJECTED', 'REFUNDED', 'CHARGED_BACK'].includes(key)) {
    return { label: key === 'REFUNDED' ? 'Estornado' : 'Cancelado', cls: 'cancelled', commCls: 'is-cancelled' };
  }
  return { label: key || '—', cls: 'other', commCls: 'is-other' };
}

/* ---- Tooltips dos cards ---- */
const KPI_HELP = {
  totalGrossSales:
    'Soma do valor dos produtos vendidos (itemPrice × quantidade) nas vendas registradas do grupo.',
  totalCommission:
    'Comissão estimada (bruta) acumulada em todas as vendas, independentemente do status.',
  availableCommission:
    'Saldo disponível: comissões COMPLETED que ainda não entraram em nenhuma fatura.',
  pendingCommission:
    'Saldo pendente: comissões aguardando validação (status PAID, APPROVED, PENDING, ON_HOLD ou WAITING).',
  totalClicks:
    'Número total de cliques em seus links encurtados (lifetime).',
  totalSalesCount:
    'Quantidade de pedidos registrados (vendas). Considera status válidos (PAID, APPROVED, PENDING, ON_HOLD, WAITING e COMPLETED).',
  vpc:
    'Valor por clique no período selecionado = (comissão do período ÷ cliques do período).',
  promosSentToday:
    'Quantidade de promoções que enviamos hoje (horário de São Paulo).',
  participants:
    'Total de participantes do seu grupo. O nome do grupo aparece abaixo.',
};

const MetricToggle = ({ active, color, label, info, valueText, onChange }) => (
  <button type="button" className={`metric-toggle ${active ? 'active' : ''}`} onClick={onChange}>
    <div className="metric-left">
      <span className="color-dot" style={{ backgroundColor: color }} />
      <span className="metric-label">
        {label}
        <span className="tooltip">
          <FiInfo size={14} className="info-icon" />
          <span className="tooltip-bubble">{info}</span>
        </span>
      </span>
    </div>
    <div className="metric-right">
      <span className="metric-value">{valueText}</span>
      <span className={`switch ${active ? 'on' : ''}`} aria-hidden />
    </div>
  </button>
);

const ChartFeedback = ({ message }) => (
  <div className="chart-feedback"><p>{message}</p></div>
);

const HighlightsCard = ({ topProducts, topLinks }) => (
  <div className="highlights-grid">
    <div className="highlight-card">
      <h3>🏆 Produtos Mais Rentáveis</h3>
      <ul className="highlight-list">
        {topProducts.length > 0 ? (
          topProducts.map((product) => (
            <li key={product._id}>
              <div className="highlight-item-main">
                <FaGift className="highlight-icon gift" />
                <span>{product._id || 'Produto sem nome'}</span>
              </div>
              <strong className="highlight-value commission">
                R$ {String((Number(product.totalCommission || 0).toFixed(2))).replace('.', ',')}
              </strong>
            </li>
          ))
        ) : (
          <p className="empty-highlight">Nenhuma venda registrada ainda.</p>
        )}
      </ul>
    </div>

    <div className="highlight-card">
      <h3>🚀 Links Mais Clicados</h3>
      <ul className="highlight-list">
        {topLinks.length > 0 ? (
          topLinks.map((link) => (
            <li key={link._id}>
              <div className="highlight-item-main">
                <FaExternalLinkAlt className="highlight-icon link" />
                <span>Link: {link.shortCode}</span>
              </div>
              <strong className="highlight-value clicks">
                {link.clickCount} cliques
              </strong>
            </li>
          ))
        ) : (
          <p className="empty-highlight">Nenhum clique registrado ainda.</p>
        )}
      </ul>
    </div>
  </div>
);

/* ===== Helpers de Período ===== */
const pad = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const dmy = (d) => `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;

/** Converte entrada (Date ou 'YYYY-MM-DD') para Date LOCAL (meia-noite) */
function toLocalDateSafe(x) {
  if (x instanceof Date) return new Date(x.getFullYear(), x.getMonth(), x.getDate());
  if (typeof x === 'string') {
    const [y, m, d] = x.split('-').map(Number);
    return new Date(y, (m || 1) - 1, d || 1);
  }
  return new Date();
}

function rangeFromPeriod(period) {
  const today = new Date();
  const onlyDate = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  let start = onlyDate(today);
  let end = onlyDate(today);

  switch (period.type) {
    case 'today':
      break;
    case 'yesterday':
      start = new Date(end.getFullYear(), end.getMonth(), end.getDate() - 1);
      end = start;
      break;
    case 'last7': { // últimos 7 dias (deslizante)
      start = new Date(end.getFullYear(), end.getMonth(), end.getDate() - 6);
      break;
    }
    case 'month':
      start = new Date(end.getFullYear(), end.getMonth(), 1);
      break;
    case 'year':
      start = new Date(end.getFullYear(), 0, 1);
      break;
    case 'custom':
      start = toLocalDateSafe(period.startDate || today);
      end   = toLocalDateSafe(period.endDate   || today);
      break;
    default:
      // fallback legado
      const dow2 = (end.getDay() + 6) % 7;
      start = new Date(end.getFullYear(), end.getMonth(), end.getDate() - dow2);
  }
  return { startDate: ymd(start), endDate: ymd(end), startObj: start, endObj: end };
}

function labelFromPeriod(period) {
  switch (period.type) {
    case 'today': return 'Hoje';
    case 'yesterday': return 'Ontem';
    case 'last7': return 'Últimos 7 dias';
    case 'month': return 'Este mês';
    case 'year': return 'Este ano';
    case 'custom': {
      if (!period.startDate || !period.endDate) return 'Personalizar';
      const sd = toLocalDateSafe(period.startDate);
      const ed = toLocalDateSafe(period.endDate);
      return `${dmy(sd)} – ${dmy(ed)}`;
    }
    default: return 'Últimos 7 dias';
  }
}

/* ---------------------------------------------------- */
export default function Dashboard() {
  const { user, logout, updateStage, updateUser } = useAuth();

  const asUserId = useMemo(getAsFromLocation, []);
  const viewingAs = Boolean(asUserId);

  const [stats, setStats] = useState(null);
  const [viewedUser, setViewedUser] = useState(null); // Dados do usuário sendo visualizado
  const [isLoading, setIsLoading] = useState(false);
  const [copiedLink, setCopiedLink] = useState(null);
  const [salesPage, setSalesPage] = useState(1);
  const [salesPageSize] = useState(8);
  const [selectedMetrics, setSelectedMetrics] = useState(['cliques', 'comissao']);
  const [isNicheModalOpen, setIsNicheModalOpen] = useState(false);

  // ➕ faltava este state
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // período padrão: últimos 7 dias
  const [period, setPeriod] = useState({ type: 'last7' });

  const isMobile = useMedia('(max-width: 640px)');

  const dateParams = useMemo(() => {
    const r = rangeFromPeriod(period);
    return { startDate: r.startDate, endDate: r.endDate };
  }, [period]);

  const authHeaders = useMemo(
    () => ({ Authorization: 'Bearer ' + (user?.token || '') }),
    [user?.token]
  );

  const finalizeOnboarding = useCallback(async () => {
    if (viewingAs) return;
    if (user && user.onboardStage < 4) {
      try {
        await axios.put(
          `${API_BASE}/api/onboarding/stage`,
          { stage: 4 },
          { headers: { Authorization: `Bearer ${user.token}` } }
        );
        updateStage(4);
      } catch (err) { console.error('Falha ao finalizar onboarding:', err); }
    }
  }, [viewingAs, user, updateStage]);

  useEffect(() => { finalizeOnboarding(); }, [finalizeOnboarding]);

  // Buscar dados do usuário sendo visualizado (quando admin está vendo outro usuário)
  const fetchViewedUser = useCallback(async () => {
    if (!viewingAs || !asUserId || !user?.token) return;
    try {
      const { data } = await axios.get(`${API_BASE}/api/admin/users/${asUserId}`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      setViewedUser(data);
    } catch (err) {
      console.error('Falha ao buscar dados do usuário visualizado:', err);
    }
  }, [viewingAs, asUserId, user?.token]);

  useEffect(() => {
    if (viewingAs) {
      fetchViewedUser();
    }
  }, [viewingAs, fetchViewedUser]);

  const fetchAnalytics = useCallback(async () => {
    if (!user?.token) return;
    setIsLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE}/api/dashboard/analytics`, {
        headers: { Authorization: `Bearer ${user.token}` },
        params: {
          t: Date.now(),
          salesPage,
          salesLimit: salesPageSize,
          asUserId: asUserId || undefined,
          startDate: dateParams.startDate,
          endDate: dateParams.endDate,
        },
      });
      setStats(data);
    } catch (err) {
      console.error('Falha ao buscar analytics:', err);
    } finally {
      setIsLoading(false);
    }
  }, [user?.token, salesPage, salesPageSize, asUserId, dateParams]);

  useEffect(() => {
    fetchAnalytics();
    const intervalId = setInterval(fetchAnalytics, 30000);
    return () => clearInterval(intervalId);
  }, [fetchAnalytics]);

  const handleCopyLink = (urlToCopy, itemId) => {
    navigator.clipboard.writeText(urlToCopy).then(() => {
      setCopiedLink(itemId);
      setTimeout(() => setCopiedLink(null), 2000);
    }).catch((err) => {
      console.error('Falha ao copiar o link: ', err);
      alert('Não foi possível copiar o link.');
    });
  };

  const toggleMetric = (id) => {
    setSelectedMetrics((prev) =>
      prev.includes(id)
        ? prev.filter((x) => x !== id)
        : prev.length >= 2
          ? [prev[prev.length - 1], id]
          : [...prev, id]
    );
  };

  const metricTotals = useMemo(() => {
    if (!stats?.chartData) return { cliques: 0, comissao: 0 };
    return stats.chartData.reduce(
      (totals, row) => {
        totals.cliques += row.cliques || 0;
        totals.comissao += row.comissao || 0;
        return totals;
      },
      { cliques: 0, comissao: 0 }
    );
  }, [stats?.chartData]);

  // VPC do período
  const vpcPeriod = useMemo(() => {
    const clicks = metricTotals.cliques || 0;
    const rev = metricTotals.comissao || 0;
    return clicks ? rev / clicks : 0;
  }, [metricTotals]);

  /* ===== Relatório diário ===== */
  const dailyRows = useMemo(() => {
    const fallback = (stats?.chartData || []).map((r) => ({
      date: r.name,
      clicks: Number(r.cliques || 0),
      revenue: Number(r.comissao || 0),
    }));

    const src = Array.isArray(stats?.dailyReport) && stats.dailyReport.length
      ? stats.dailyReport.map((r) => ({
        date: r.date || r.dateLabel || r.dateISO || r.name,
        clicks: Number(r.clicks ?? r.cliques ?? 0),
        revenue: Number(r.revenue ?? r.commission ?? r.comissao ?? 0),
      }))
      : fallback;

    return src.map((r) => {
      const clicks = r.clicks;
      const revenue = r.revenue;
      const vpc = clicks ? revenue / clicks : 0;
      return { date: r.date, clicks, revenue, vpc };
    });
  }, [stats?.dailyReport, stats?.chartData]);

  /* ===== Chart settings ===== */
  const needsDualAxis =
    selectedMetrics.length === 2 &&
    selectedMetrics.includes('cliques') &&
    selectedMetrics.includes('comissao');

  const isMobileAxis = useMedia('(max-width: 640px)');
  const leftAxisWidth = isMobileAxis ? 30 : 40;
  const rightAxisWidth = isMobileAxis ? 30 : 40;
  const chartMargins = {
    top: 10,
    bottom: 20,
    left: isMobileAxis ? 6 : 12,
    right: needsDualAxis ? (isMobileAxis ? 10 : 18) : 6,
  };

  const totalSalesCount = stats?.sales?.salesCount || 0;
  const periodLabel = useMemo(() => {
    if (period.type === 'custom' && period.startDate && period.endDate) {
      return labelFromPeriod({ ...period });
    }
    return labelFromPeriod(period);
  }, [period]);

  const promosSentToday = useMemo(() => {
    const n = stats?.promosSentToday;
    return typeof n === 'number' ? n : 0;
  }, [stats?.promosSentToday]);

  if (!stats) return <EmojiLoader />;

  /* ===== CSV ===== */
  const exportDailyCSV = () => {
    const header = ['Data', 'Cliques', 'Comissão', 'VPC (R$/clique)'];
    const rows = dailyRows.map(r => [
      r.date,
      r.clicks,
      Number(r.revenue).toFixed(2).replace('.', ','),
      (r.vpc || 0).toFixed(2).replace('.', ','),
    ]);
    const csv = [header, ...rows].map(cols => cols.join(';')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `relatorio-diario_${dateParams.startDate}_${dateParams.endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div className="header-text">
          <h1>Olá, {user?.name}!</h1>
          <p>Acompanhe a performance do seu grupo.</p>
          {stats?.viewAsUser && (
            <div style={{ marginTop: 8 }}>
              <span className="sales-pill" title={stats.viewAsUser.email || ''}>
                Visualizando como: {stats.viewAsUser.name || stats.viewAsUser.email || stats.viewAsUser.id}
              </span>
            </div>
          )}
        </div>

        <div className="header-buttons" style={{ gap: '0.75rem', flexWrap: 'wrap' }}>
          <button
            className="period-chip"
            onClick={() => setIsFilterOpen(true)}
            title="Ajustar período"
          >
            <span className="period-chip-text">{periodLabel}</span>
            <FiChevronDown className="period-chip-caret" />
          </button>

          <button className="apply-btn" onClick={fetchAnalytics} disabled={isLoading} title="Atualizar dados">
            <FaSync className={isLoading ? 'spinning' : ''} />
            Atualizar
          </button>

          <button className="btn-ghost niche-btn" onClick={() => setIsNicheModalOpen(true)}>
            <FaTags />
            Categorias
          </button>

          <a
            className="btn-ghost whats"
            href="https://wa.me/5511934459150?text=Oi%20equipe!%20Preciso%20de%20ajuda%20com%20o%20app.%20(Enviado%20pela%20Dashboard)"
            target="_blank"
            rel="noreferrer"
            title="Falar com o suporte no WhatsApp"
          >
            💬 Suporte
          </a>

          <a
            className="btn-ghost community"
            href="https://chat.whatsapp.com/BL6rWoFnD372kpXMIKDBlH"
            target="_blank"
            rel="noreferrer"
            title="Entrar na comunidade no WhatsApp"
          >
            👥 Comunidade
          </a>

          <a
            className="btn-ghost wallet"
            href={asUserId ? `/wallet?as=${asUserId}` : '/wallet'}
            title="Ir para a carteira"
          >
            💼 Carteira
          </a>

          <button onClick={logout} className="logout-button">Sair</button>
        </div>
      </header>

      <main className="dashboard-main">
        {/* KPIs */}
        <section className="kpi-grid">
          <div className="kpi-card">
            <h2 className="kpi-value">
              R$ {Number(stats.totalGrossSales || 0).toLocaleString('pt-BR', {
                minimumFractionDigits: 2, maximumFractionDigits: 2
              })}
            </h2>
            <p className="kpi-label">
              Valor Total de Vendas
              <span className="tooltip kpi-i">
                <FiInfo size={14} className="info-icon" />
                <span className="tooltip-bubble">{KPI_HELP.totalGrossSales}</span>
              </span>
            </p>
          </div>

          <div className="kpi-card">
            <h2 className="kpi-value">
              R$ {Number(stats.totalCommission || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </h2>
            <p className="kpi-label">
              Comissão Estimada (bruta)
              <span className="tooltip kpi-i">
                <FiInfo size={14} className="info-icon" />
                <span className="tooltip-bubble">{KPI_HELP.totalCommission}</span>
              </span>
            </p>
          </div>

          <div className="kpi-card">
            <h2 className="kpi-value">
              R$ {Number(stats.availableCommission ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </h2>
            <p className="kpi-label">
              Saldo liberado (COMPLETED)
              <span className="tooltip kpi-i">
                <FiInfo size={14} className="info-icon" />
                <span className="tooltip-bubble">{KPI_HELP.availableCommission}</span>
              </span>
            </p>
          </div>

          <div className="kpi-card">
            <h2 className="kpi-value">
              R$ {Number(stats.pendingCommission ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </h2>
            <p className="kpi-label">
              Saldo Pendente
              <span className="tooltip kpi-i">
                <FiInfo size={14} className="info-icon" />
                <span className="tooltip-bubble">{KPI_HELP.pendingCommission}</span>
              </span>
            </p>
          </div>

          <div className="kpi-card">
            <h2 className="kpi-value">{(stats.totalClicks || 0).toLocaleString('pt-BR')}</h2>
            <p className="kpi-label">
              Cliques Totais
              <span className="tooltip kpi-i">
                <FiInfo size={14} className="info-icon" />
                <span className="tooltip-bubble">{KPI_HELP.totalClicks}</span>
              </span>
            </p>
          </div>

          <div className="kpi-card">
            <h2 className="kpi-value">{(stats?.sales?.salesCount || 0).toLocaleString('pt-BR')}</h2>
            <p className="kpi-label">
              Vendas Totais
              <span className="tooltip kpi-i">
                <FiInfo size={14} className="info-icon" />
                <span className="tooltip-bubble">{KPI_HELP.totalSalesCount}</span>
              </span>
            </p>
          </div>

          <div className="kpi-card">
            <h2 className="kpi-value">
              R$ {vpcPeriod.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </h2>
            <p className="kpi-label">
              VPC (R$/clique)
              <span className="tooltip kpi-i">
                <FiInfo size={14} className="info-icon" />
                <span className="tooltip-bubble">{KPI_HELP.vpc}</span>
              </span>
            </p>
          </div>

          {/* Promoções enviadas hoje */}
          <div className="kpi-card">
            <h2 className="kpi-value">{promosSentToday.toLocaleString('pt-BR')}</h2>
            <p className="kpi-label">
              Promoções Enviadas Hoje
              <span className="tooltip kpi-i">
                <FiInfo size={14} className="info-icon" />
                <span className="tooltip-bubble">{KPI_HELP.promosSentToday}</span>
              </span>
            </p>
          </div>

          <div className="kpi-card group-card">
            <div className="group-card-main">
              <h2 className="kpi-value">{stats.groupParticipantsCount || 0}</h2>
              <p className="kpi-label">
                Participantes
                <span className="tooltip kpi-i">
                  <FiInfo size={14} className="info-icon" />
                  <span className="tooltip-bubble">{KPI_HELP.participants}</span>
                </span>
              </p>
            </div>
            <div className="group-card-footer">{stats.groupName || '—'}</div>
          </div>
        </section>

        {/* Gráfico */}
        <section className="chart-section-card">
          <div className="chart-wrap">
            <div className="chart-area">
              <h3>Performance do Período</h3>
              {!stats.chartData || stats.chartData.length === 0 ? (
                <ChartFeedback message="Ainda não há dados para exibir o gráfico." />
              ) : (
                <div className="chart-box">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      key={selectedMetrics.slice().sort().join('-')}
                      data={stats.chartData}
                      margin={chartMargins}
                      baseValue={0}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} tickMargin={8} padding={{ left: 0, right: 0 }} />
                      {selectedMetrics.includes('cliques') && (
                        <YAxis
                          yAxisId="left"
                          width={isMobileAxis ? 30 : 40}
                          stroke={METRICS.find((m) => m.id === 'cliques')?.color || '#4f46e5'}
                          tick={{ fontSize: isMobileAxis ? 10 : 12 }}
                          tickMargin={isMobileAxis ? 2 : 4}
                          domain={[0, 'auto']}
                          allowDecimals={false}
                        />
                      )}
                      {selectedMetrics.includes('comissao') && (
                        <YAxis
                          yAxisId={needsDualAxis ? 'right' : 'left'}
                          orientation={needsDualAxis ? 'right' : 'left'}
                          width={isMobileAxis ? 30 : 40}
                          stroke={METRICS.find((m) => m.id === 'comissao')?.color || '#10b981'}
                          tick={{ fontSize: isMobileAxis ? 10 : 12 }}
                          tickMargin={isMobileAxis ? 2 : 4}
                          axisLine
                          tickLine={false}
                          domain={[0, 'auto']}
                          tickFormatter={(v) => `R$ ${Number(v).toLocaleString('pt-BR')}`}
                        />
                      )}
                      <Tooltip
                        formatter={(value, name) => {
                          const m = METRICS.find((m) => m.id === name);
                          return m ? [m.format(value), m.label] : [value, name];
                        }}
                      />
                      <Legend formatter={(value) => METRICS.find((m) => m.id === value)?.label || value} />
                      <defs>
                        {METRICS.map((m) => (
                          <linearGradient key={m.id} id={`grad-${m.id}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={m.color} stopOpacity={0.35} />
                            <stop offset="95%" stopColor={m.color} stopOpacity={0} />
                          </linearGradient>
                        ))}
                      </defs>
                      {selectedMetrics.map((id) => {
                        const metric = METRICS.find((m) => m.id === id);
                        if (!metric) return null;
                        const yAxisId = id === 'comissao' && needsDualAxis ? 'right' : 'left';
                        return (
                          <Area
                            key={id}
                            type="monotone"
                            dataKey={id}
                            stroke={metric.color}
                            fillOpacity={1}
                            fill={`url(#grad-${id})`}
                            yAxisId={yAxisId}
                            strokeWidth={2}
                            connectNulls
                          />
                        );
                      })}
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            <aside className="metric-list">
              <p className="metric-hint">Selecione para comparar:</p>
              <div className="metric-col">
                {METRICS.map((m) => (
                  <MetricToggle
                    key={m.id}
                    active={selectedMetrics.includes(m.id)}
                    color={m.color}
                    label={m.label}
                    info={m.info}
                    valueText={m.format(metricTotals[m.id])}
                    onChange={() => toggleMetric(m.id)}
                  />
                ))}
              </div>
            </aside>
          </div>
        </section>

        {/* Relatório Diário */}
        <section className="report-card">
          <div className="report-header">
            <h3>Relatório diário de cliques e ganhos</h3>
            <div className="report-actions">
              <span className="sales-pill">{(stats.chartData?.length || 0)} dias</span>
              <button className="ghost" onClick={exportDailyCSV} title="Baixar CSV">
                <FaDownload /> Exportar CSV
              </button>
            </div>
          </div>

          {!dailyRows.length ? (
            <p className="empty-highlight">Sem registros no período selecionado.</p>
          ) : (
            <div className="table-wrap">
              <table className="report-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th className="right">Cliques</th>
                    <th className="right">Comissão</th>
                    <th className="right">VPC</th>
                  </tr>
                </thead>
                <tbody>
                  {dailyRows.map((r, idx) => (
                    <tr key={`${r.date}-${idx}`}>
                      <td>{r.date}</td>
                      <td className="right">{r.clicks.toLocaleString('pt-BR')}</td>
                      <td className="right">R$ {r.revenue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td className="right">R$ {r.vpc.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {stats.topProductsByCommission && stats.topLinksByClicks && (
          <HighlightsCard
            topProducts={stats.topProductsByCommission}
            topLinks={stats.topLinksByClicks}
          />
        )}

        {stats?.sales?.hasSales && (
          <section className="sales-card">
            <div className="sales-card-header">
              <h3>Produtos Vendidos</h3>
              <span className="sales-pill">{stats.sales.salesCount} pedidos</span>
            </div>

            <div className="sales-list">
              {stats.sales.recentSales.map((s) => {
                const meta = getStatusMeta(s.status);
                return (
                  <div key={s.orderId} className="sale-row">
                    <div className="sale-main">
                      <p className="sale-title">{s.productName}</p>
                      <div className="sale-meta">
                        <span className="sale-qty">Qtd: {s.quantity}</span>
                        <span className="sale-shop">{s.shopName}</span>
                      </div>
                    </div>

                    <div className="sale-right">
                      <span className={`sale-status sale-status-block ${meta.cls}`}>{meta.label}</span>
                      <strong className={`sale-commission ${meta.commCls}`}>
                        R$ {String((s.commissionAmount ?? 0).toFixed(2)).replace('.', ',')}
                      </strong>
                      <span className="sale-price-small">
                        Valor: R$ {String(s.itemPrice || '0.00').replace('.', ',')}
                      </span>
                      <span className="sale-time">{new Date(s.orderTime).toLocaleString()}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            {(stats.sales.page > 1 || stats.sales.hasMore) && (
              <div className="sales-pagination">
                <button
                  className="sales-page-btn"
                  disabled={isLoading || stats.sales.page <= 1}
                  onClick={() => setSalesPage((p) => Math.max(1, p - 1))}
                >
                  ← Anteriores
                </button>
                <span className="sales-page-indicator">Página {stats.sales.page}</span>
                <button
                  className="sales-page-btn"
                  disabled={isLoading || !stats.sales.hasMore}
                  onClick={() => setSalesPage((p) => p + 1)}
                >
                  Próximas →
                </button>
              </div>
            )}
          </section>
        )}

        <section className="live-feed-card">
          <h3>Últimas Promoções Enviadas</h3>
          <div className="feed-list">
            {stats.recentFeed.length === 0 && (
              <p className="empty-highlight">Aguardando novas promoções enviadas...</p>
            )}

            {stats.recentFeed.map((item) => (
              <div key={item._id} className="feed-row">
                <div className="feed-left">
                  {item.imageUrl ? (
                    <img className="feed-thumb" src={item.imageUrl} alt="" loading="lazy" />
                  ) : (
                    <div className="feed-thumb placeholder">🖼️</div>
                  )}
                </div>

                <div className="feed-center">
                  <p className="feed-title">{item.title}</p>
                </div>

                <div className="feed-right">
                  <div className="feed-commission">
                    <img
                      className="brand-icon"
                      src={item.source === 'Shopee' ? '/shopee.svg' : '/amazon.svg'}
                      alt={item.source || ''}
                      loading="lazy"
                    />
                    <span className="label">Comissão Est.</span>
                    <strong>R$ {String(item.estimatedCommission || '0.00').replace('.', ',')}</strong>
                  </div>

                  {item.trackedUrl && (
                    <button
                      className={`copy-link-ghost ${copiedLink === item._id ? 'copied' : ''}`}
                      onClick={() => handleCopyLink(item.trackedUrl, item._id)}
                    >
                      {copiedLink === item._id ? 'Copiado' : 'Copiar link'}
                    </button>
                  )}

                  <span className="feed-time">{new Date(item.timestamp).toLocaleTimeString()}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>

      {/* Modal de Filtros (período) */}
      <FilterModal
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        currentPeriod={{
          type: period.type,
          startDate: period.startDate instanceof Date ? period.startDate : undefined,
          endDate: period.endDate instanceof Date ? period.endDate : undefined,
        }}
        onSave={(p) => {
          if (p.type === 'custom') {
            setPeriod({ type: 'custom', startDate: p.startDate, endDate: p.endDate });
          } else {
            setPeriod({ type: p.type });
          }
          setIsFilterOpen(false);
        }}
      />

      <NicheModal
        isOpen={isNicheModalOpen}
        onClose={async (shouldRefresh) => {
          setIsNicheModalOpen(false);
          // Se foi salvo com sucesso, recarrega os dados do usuário
          if (shouldRefresh) {
            try {
              if (viewingAs) {
                // Se está visualizando outro usuário, recarregar dados dele
                await fetchViewedUser();
              } else {
                // Se é o próprio usuário, recarregar do /api/me
                const { data } = await axios.get(`${API_BASE}/api/me`, { headers: authHeaders });
                updateUser(data);
              }
            } catch (err) {
              console.error('Erro ao recarregar dados do usuário:', err);
            }
          }
        }}
        user={user}
        authHeaders={authHeaders}
        viewedUser={viewingAs ? viewedUser : null}
      />
    </div>
  );
}